import { useState } from 'react';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'What types of moving services does City to City Moving Company offer?',
      answer: 'We offer comprehensive moving services including local and long-distance moving, packing and unpacking, loading and unloading, commercial moving, and specialty item transport for pianos, antiques, and fine art. We also provide picture hanging, TV mounting, and furniture assembly services.',
      icon: 'ri-truck-line',
      color: 'text-amber-600',
    },
    {
      question: 'Is City to City Moving Company licensed and insured?',
      answer: 'Yes, we are fully licensed and insured to protect your belongings during every move. Our insurance coverage ensures your peace of mind throughout the entire moving process.',
      icon: 'ri-shield-check-line',
      color: 'text-emerald-600',
    },
    {
      question: 'What areas does City to City Moving Company serve?',
      answer: 'We provide moving services throughout Southern California including Irvine, Indio, Newport Beach, and surrounding areas in Orange County and the Coachella Valley. We also offer long-distance moving services across the entire United States.',
      icon: 'ri-map-pin-line',
      color: 'text-sky-600',
    },
    {
      question: 'How much does a move cost?',
      answer: 'Moving costs vary based on factors like distance, size of the move, services needed, and timing. We provide free, detailed estimates with transparent pricing and no hidden fees. Contact us for a personalized quote.',
      icon: 'ri-money-dollar-circle-line',
      color: 'text-violet-600',
    },
    {
      question: 'How far in advance should I book my move?',
      answer: 'We recommend booking 2-4 weeks in advance, especially during peak moving season (summer months). However, we also offer same-day service when available for urgent moves.',
      icon: 'ri-calendar-check-line',
      color: 'text-rose-600',
    },
    {
      question: 'Do you provide packing materials?',
      answer: 'Yes, we provide all necessary packing materials including boxes, tape, bubble wrap, and protective padding. We can also pack your belongings for you as part of our full-service moving package.',
      icon: 'ri-box-3-line',
      color: 'text-amber-600',
    },
    {
      question: 'What items can you not move?',
      answer: 'For safety reasons, we cannot transport hazardous materials, flammable items, explosives, perishable food, plants, or pets. We\'ll provide a complete list of restricted items when you book your move.',
      icon: 'ri-alert-line',
      color: 'text-red-600',
    },
    {
      question: 'Do you offer storage solutions?',
      answer: 'Yes, we can arrange short-term and long-term storage solutions if needed. Contact us to discuss your storage requirements and we\'ll find the best solution for your needs.',
      icon: 'ri-archive-line',
      color: 'text-sky-600',
    },
  ];

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-bold mb-4">
            <i className="ri-question-answer-line mr-2"></i>Got Questions?
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Everything You Need to Know<br />About Moving with Us
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Quick answers to the most common questions our neighbors ask. Can't find what you're looking for? Give us a call!
          </p>
        </div>

        {/* FAQ Grid - Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className={`bg-slate-50 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 ${
                openIndex === index ? 'ring-2 ring-amber-400' : ''
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-start gap-4 p-6 text-left hover:bg-slate-100 transition-colors"
              >
                <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${faq.color} bg-white rounded-lg shadow-sm`}>
                  <i className={`${faq.icon} text-xl`}></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-base font-bold text-slate-900 pr-8 leading-snug">{faq.question}</h4>
                </div>
                <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 bg-white rounded-full shadow-sm transition-transform ${
                  openIndex === index ? 'rotate-180' : ''
                }`}>
                  <i className="ri-arrow-down-s-line text-xl text-slate-600"></i>
                </div>
              </button>
              {openIndex === index && (
                <div className="px-6 pb-6 pl-20">
                  <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact CTA - Horizontal Layout */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 lg:p-10 shadow-xl">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="text-white">
              <h3 className="text-3xl font-bold mb-3">Still Have Questions?</h3>
              <p className="text-slate-300 text-lg leading-relaxed">
                Our friendly team is here to help you plan your move. We're just a phone call away and ready to answer any questions you have.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:7606257767" 
                className="flex-1 bg-amber-500 text-white px-6 py-4 rounded-xl font-bold hover:bg-amber-600 transition-colors whitespace-nowrap text-center flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>Call (760) 625-7767</span>
              </a>
              <a 
                href="#quote-form" 
                className="flex-1 bg-white text-slate-900 px-6 py-4 rounded-xl font-bold hover:bg-slate-100 transition-colors whitespace-nowrap text-center flex items-center justify-center gap-2"
              >
                <i className="ri-file-list-3-line text-xl"></i>
                <span>Get Free Quote</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}